import { useState } from 'react';
import { AlertCircle, CheckCircle2, ExternalLink, FileCheck2, Info, Loader2, RotateCcw, Send, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import type { TranzilaSettings } from '@/lib/tranzilaSettings';
import { TranzilaPaymentFields, paymentError, paymentFromSettings, type TranzilaPayment } from './TranzilaPaymentFields';
import { TranzilaEmailButton } from './TranzilaEmailButton';
import { RivhitCustomerPickerModal, parseRivhitCustomerRequest, type RivhitCustomerChoice, type RivhitCustomerRequest } from './RivhitCustomerPickerModal';

export type ExternalProvider = 'tranzila' | 'rivhit';

export interface ExternalIssued { number: string; url: string | null }

const META: Record<ExternalProvider, { label: string; button: string; badge: string }> = {
  tranzila: {
    label:  'טרנזילה',
    button: 'bg-teal-600 hover:bg-teal-700',
    badge:  'bg-teal-50 text-teal-800 border-teal-200 hover:bg-teal-100',
  },
  rivhit: {
    label:  'רווחית',
    button: 'bg-sky-600 hover:bg-sky-700',
    badge:  'bg-sky-50 text-sky-800 border-sky-200 hover:bg-sky-100',
  },
};

const fmtMoney = (n: number) => `₪${n.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

type EmailOutcome = { status: 'off' | 'sent' | 'failed' | 'no_email'; to?: string; error?: string };

const EMAIL_NOTICE: Record<Exclude<EmailOutcome['status'], 'off'>, { tone: string; text: (o: EmailOutcome) => string }> = {
  sent:     { tone: 'bg-emerald-50 border-emerald-200 text-emerald-800', text: o => `המסמך נשלח במייל אל ${o.to ?? 'הלקוח'}.` },
  no_email: { tone: 'bg-amber-50 border-amber-200 text-amber-800',       text: () => 'ללקוח אין כתובת מייל, ולכן המסמך לא נשלח. אפשר לשלוח אותו ידנית מהמסמך.' },
  failed:   { tone: 'bg-red-50 border-red-200 text-red-700',             text: o => o.error ?? 'שליחת המייל נכשלה. אפשר לנסות שוב מהמסמך.' },
};

interface Props {
  docId:            string;
  docLabel:         string;
  clientName:       string | null;
  clientEmail:      string | null;
  total:            number;
  lineCount:        number | null;
  enabled:          ExternalProvider[];
  credit:           boolean;
  tranzilaSettings: TranzilaSettings | null;
  issued:           Partial<Record<ExternalProvider, ExternalIssued>>;
  disabled?:        boolean;
  onIssued:         (provider: ExternalProvider, result: ExternalIssued) => void;
}

export function ExternalInvoiceActions({ docId, docLabel, clientName, clientEmail, total, lineCount, enabled, credit, tranzilaSettings, issued, disabled, onIssued }: Props) {
  const [confirming, setConfirming] = useState<ExternalProvider | null>(null);
  const [sending,    setSending]    = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const [payment,    setPayment]    = useState<TranzilaPayment | null>(null);
  const [result,     setResult]     = useState<{ number: string; url: string | null; email: EmailOutcome; related: string | null } | null>(null);
  const [emailKey,   setEmailKey]   = useState(0);
  const [picker,     setPicker]     = useState<RivhitCustomerRequest | null>(null);
  const [pickerErr,  setPickerErr]  = useState<string | null>(null);

  const providers = (['tranzila', 'rivhit'] as ExternalProvider[]).filter(p => enabled.includes(p) || issued[p]);
  if (providers.length === 0) return null;

  const tzDocType = tranzilaSettings ? (credit ? tranzilaSettings.credit_document_type : tranzilaSettings.default_document_type) : null;
  const editablePayment = (p: ExternalProvider) => p === 'tranzila' && !!tranzilaSettings?.ask_payment_each_time && tzDocType === 'IR';
  const actionWord = credit ? 'זיכוי' : 'חשבונית';

  const open = (p: ExternalProvider) => {
    setError(null); setResult(null);
    setPayment(editablePayment(p) && tranzilaSettings ? paymentFromSettings(tranzilaSettings, credit) : null);
    setConfirming(p);
  };

  const close = () => {
    if (sending) return;
    setConfirming(null); setError(null); setResult(null); setPayment(null); setPicker(null); setPickerErr(null);
  };

  const send = async (customer?: RivhitCustomerChoice) => {
    if (!confirming) return;
    if (payment) {
      const invalid = paymentError(payment);
      if (invalid) { setError(invalid); return; }
    }
    setSending(true); setError(null); setPickerErr(null);
    const { data, error: e } = await supabase.functions.invoke('external-invoice', {
      body: { action: 'push', provider: confirming, document_id: docId, ...(payment ? { payment } : {}), ...(customer ? { rivhit_customer: customer } : {}) },
    });
    setSending(false);
    const request = parseRivhitCustomerRequest(data);
    if (request) { setPicker(request); return; }
    if (data?.ok && typeof data.number === 'string') {
      const url = typeof data.url === 'string' ? data.url : null;
      const email: EmailOutcome = data.email && typeof data.email.status === 'string' ? data.email : { status: 'off' };
      onIssued(confirming, { number: data.number, url });
      setEmailKey(k => k + 1);
      setPicker(null);
      setResult({ number: data.number, url, email, related: typeof data.related_number === 'string' ? data.related_number : null });
      return;
    }
    const message = typeof data?.error === 'string' ? data.error : e ? 'לא הצלחנו להתחבר לשרת. נסו שוב בעוד רגע.' : 'ההפקה נכשלה. נסו שוב.';
    if (picker) setPickerErr(message); else setError(message);
  };

  return (
    <>
      {providers.map(p => {
        const done = issued[p];
        if (done) {
          const content = (<><FileCheck2 className="w-3.5 h-3.5" />{credit ? 'זיכוי ' : ''}{META[p].label} #{done.number}{done.url && <ExternalLink className="w-3 h-3" />}</>);
          return (
            <span key={p} className="flex items-center gap-1.5">
              {done.url ? (
                <a href={done.url} target="_blank" rel="noopener noreferrer" title={`פתח את המסמך ב${META[p].label}`}
                   className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${META[p].badge}`}>
                  {content}
                </a>
              ) : (
                <span className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border ${META[p].badge}`}>{content}</span>
              )}
              {p === 'tranzila' && done.url && <TranzilaEmailButton docId={docId} clientEmail={clientEmail} refreshKey={emailKey} />}
            </span>
          );
        }
        return (
          <button
            key={p}
            onClick={() => open(p)}
            disabled={disabled}
            className={`flex items-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${credit ? 'bg-rose-600 hover:bg-rose-700' : META[p].button}`}
          >
            {credit ? <RotateCcw className="w-4 h-4" /> : <Send className="w-4 h-4" />}
            {credit ? `הפק זיכוי ב${META[p].label}` : `הפק ב${META[p].label}`}
          </button>
        );
      })}

      {confirming && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4" dir="rtl" onClick={close}>
          <div className="w-full max-w-md max-h-[90vh] flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-base font-bold text-gray-900">{result ? 'המסמך הופק' : `הפקת ${actionWord} ב${META[confirming].label}`}</h2>
              <button onClick={close} disabled={sending} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {result ? (
              <div className="px-6 py-5 space-y-4 overflow-y-auto">
                <div className="flex items-center gap-3 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                  <div className="text-sm text-emerald-900">
                    <p className="font-semibold">{actionWord} מס' {result.number} הופקה ב{META[confirming].label}</p>
                    {result.related && <p className="text-xs mt-0.5">מקושרת לחשבונית המקורית מס' {result.related}</p>}
                  </div>
                </div>
                {result.email.status !== 'off' && (
                  <div className={`flex items-start gap-2 px-4 py-3 border rounded-xl text-sm ${EMAIL_NOTICE[result.email.status].tone}`}>
                    <Info className="w-4 h-4 shrink-0 mt-0.5" />{EMAIL_NOTICE[result.email.status].text(result.email)}
                  </div>
                )}
                {result.url && (
                  <a href={result.url} target="_blank" rel="noopener noreferrer"
                     className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-teal-700 border border-teal-200 rounded-lg hover:bg-teal-50 transition-colors">
                    <ExternalLink className="w-4 h-4" />פתח את המסמך
                  </a>
                )}
              </div>
            ) : (
              <div className="px-6 py-5 space-y-4 overflow-y-auto">
                <p className="text-sm text-gray-600 leading-relaxed">
                  {credit
                    ? `תיווצר חשבונית זיכוי אמיתית ב${META[confirming].label} עבור השורות שהוחזרו. לא ניתן לבטל את הפעולה מכאן, ואפשר להפיק פעם אחת בלבד לכל תעודה.`
                    : `ייווצר מסמך אמיתי ב${META[confirming].label} לפי הגדרות החיבור. לא ניתן לבטל את הפעולה מכאן, ואפשר להפיק פעם אחת בלבד לכל תעודה.`}
                </p>
                <dl className="grid grid-cols-2 gap-y-2 text-sm bg-gray-50 border border-gray-100 rounded-xl px-4 py-3">
                  <dt className="text-gray-500">תעודה</dt><dd className="font-medium text-gray-900">{docLabel}</dd>
                  <dt className="text-gray-500">לקוח</dt><dd className="font-medium text-gray-900">{clientName ?? 'ללא לקוח'}</dd>
                  {lineCount !== null && (<><dt className="text-gray-500">שורות מוצרים</dt><dd className="font-medium text-gray-900">{lineCount}</dd></>)}
                  <dt className="text-gray-500">{credit ? 'סה"כ לזיכוי' : 'סה"כ כולל מע"מ'}</dt><dd className="font-semibold text-gray-900">{fmtMoney(total)}</dd>
                </dl>
                {payment && <TranzilaPaymentFields value={payment} onChange={setPayment} credit={credit} disabled={sending} />}
                {confirming === 'tranzila' && tranzilaSettings?.auto_email && (
                  <p className="flex items-center gap-1.5 text-xs text-gray-500">
                    <Info className="w-3.5 h-3.5 shrink-0" />
                    {clientEmail ? `המסמך יישלח במייל אל ${clientEmail}` : 'ללקוח אין כתובת מייל, המסמך לא יישלח אוטומטית'}
                  </p>
                )}
                {error && (
                  <div className="flex items-start gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />{error}
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 px-6 py-4 bg-gray-50 border-t border-gray-100">
              {result ? (
                <button onClick={close} className="px-4 py-2 text-sm font-medium text-white bg-gray-800 hover:bg-gray-900 rounded-lg transition-colors">
                  סגור
                </button>
              ) : (
                <>
                  <button onClick={close} disabled={sending} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50">
                    ביטול
                  </button>
                  <button onClick={() => send()} disabled={sending}
                          className={`flex items-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60 ${credit ? 'bg-rose-600 hover:bg-rose-700' : META[confirming].button}`}>
                    {sending ? <><Loader2 className="w-4 h-4 animate-spin" />מפיק…</> : <><Send className="w-4 h-4" />הפק עכשיו</>}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {picker && (
        <RivhitCustomerPickerModal
          request={picker}
          busy={sending}
          error={pickerErr}
          onChoose={send}
          onCancel={() => { if (!sending) { setPicker(null); setPickerErr(null); setConfirming(null); } }}
        />
      )}
    </>
  );
}
